import { useState, useEffect } from 'react'
import { badgeService } from '../services'
import {
  TrophyIcon,
  StarIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolid } from '@heroicons/react/24/solid'

export default function Badges() {
  const [badgeProgress, setBadgeProgress] = useState({})
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('all')

  useEffect(() => {
    fetchBadges()
  }, [])

  const fetchBadges = async () => {
    try {
      setLoading(true)
      const data = await badgeService.getProgress()
      setBadgeProgress(data.badge_progress)
      setStats(data.stats)
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false)
    }
  }

  const getCategoryLabel = (category) => {
    const labels = {
      beginner: 'Getting Started',
      achievement: 'Achievements',
      mastery: 'Mastery',
      special: 'Special',
    }
    return labels[category] || category
  }

  const getCategoryIcon = (category) => {
    const icons = {
      beginner: '🎯',
      achievement: '🏆',
      mastery: '👑',
      special: '⭐',
    }
    return icons[category] || '🏅'
  }

  const filteredBadges = activeCategory === 'all' 
    ? Object.entries(badgeProgress).flatMap(([cat, badges]) => 
        badges.map(badge => ({ ...badge, category: cat }))
      )
    : badgeProgress[activeCategory] || []

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading badges...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-8 text-white">
        <div className="flex items-center mb-4">
          <TrophyIcon className="h-10 w-10 mr-3" />
          <h1 className="text-4xl font-bold">Achievement Badges</h1>
        </div>
        <p className="text-yellow-100 text-lg max-w-3xl mb-6">
          Earn badges as you progress through the course and unlock special achievements!
        </p>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm">Badges Earned</p>
                  <p className="text-3xl font-bold">{stats.earned_badges}/{stats.total_badges}</p>
                </div>
                <TrophyIcon className="h-8 w-8 text-yellow-200" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm">Total Points</p>
                  <p className="text-3xl font-bold">{stats.total_points}</p>
                </div>
                <StarSolid className="h-8 w-8 text-yellow-200" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm">Completion</p>
                  <p className="text-3xl font-bold">{stats.completion_percentage}%</p>
                </div>
                <div className="text-yellow-200 text-2xl">🎯</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
            activeCategory === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Badges
        </button>
        {Object.keys(badgeProgress).map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center ${
              activeCategory === category
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span className="mr-2">{getCategoryIcon(category)}</span>
            {getCategoryLabel(category)}
          </button>
        ))}
      </div>

      {/* Badges Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBadges.map((badge) => (
          <div
            key={badge.id}
            className={`relative bg-white rounded-xl border-2 p-6 transition-all ${
              badge.earned
                ? 'border-yellow-400 shadow-lg hover:shadow-xl'
                : 'border-gray-200 opacity-60 hover:opacity-100'
            }`}
          >
            {/* Earned Badge Banner */}
            {badge.earned && (
              <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                EARNED ✓
              </div>
            )}

            {/* Lock Icon for Unearned */}
            {!badge.earned && (
              <div className="absolute top-4 right-4 text-gray-400">
                <LockClosedIcon className="h-6 w-6" />
              </div>
            )}

            {/* Badge Icon */}
            <div className={`text-6xl mb-4 ${!badge.earned && 'grayscale'}`}>
              {badge.icon}
            </div>

            {/* Badge Info */}
            <h3 className={`text-xl font-bold mb-2 ${badge.earned ? 'text-gray-900' : 'text-gray-500'}`}>
              {badge.name}
            </h3>
            <p className={`text-sm mb-4 ${badge.earned ? 'text-gray-600' : 'text-gray-400'}`}>
              {badge.description}
            </p>

            {/* Points */}
            <div className={`flex items-center justify-between pt-4 border-t ${
              badge.earned ? 'border-gray-200' : 'border-gray-100'
            }`}>
              <div className="flex items-center">
                <StarIcon className={`h-5 w-5 mr-1 ${badge.earned ? 'text-yellow-500' : 'text-gray-400'}`} />
                <span className={`font-semibold ${badge.earned ? 'text-gray-900' : 'text-gray-400'}`}>
                  {badge.points} points
                </span>
              </div>
              <span
                className="text-xs px-2 py-1 rounded-full font-semibold"
                style={{
                  backgroundColor: badge.earned ? `${badge.color}20` : '#f3f4f6',
                  color: badge.earned ? badge.color : '#9ca3af',
                }}
              >
                {getCategoryLabel(badge.category || activeCategory)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {filteredBadges.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <TrophyIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p>No badges in this category yet.</p>
        </div>
      )}
    </div>
  )
}
